import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { Box, Stack, Typography, useTheme, darken, styled } from "@mui/material";
import { useSnackbar } from "notistack";

import { ReactComponent as PeopleIcon } from "assets/svg/people.svg";

import { TaskActivityCard } from "user-journey/components/journey-activity/cards/TaskActivityCard";
import { ActivityStatus, JourneyActivityType } from "user-journey/consts/journeyActivity";

import { getInvertColor } from "utils/themeUtils";
import { apiClient } from "utils/apiClient";
import { getErrorMessage } from "utils/errorUtils";

import { localStorageOrgSlugKey } from "auth/helpers/authApi";

import Preloader from "components/PreLoader";

import "./index.css";

const OuterContainer = styled(Stack)(({ theme }) => ({
	position: "relative",
	justifyContent: "center",
	padding: `${theme.spacing(6)} ${theme.spacing(16)}`,
	width: "100%",
	minHeight: "100%",
	flex: "1",
	backgroundColor: theme.palette.legacyPrimary["100"],

	"@media (max-width: 80em)": {
		paddingLeft: theme.spacing(8),
		paddingRight: theme.spacing(8),
	},
	[theme.breakpoints.down("sm")]: {
		padding: 0,
	},
}));

const Container = styled(Box)(({ theme }) => ({
	position: "relative",
	display: "flex",
	flexDirection: "column",

	padding: theme.spacing(6),
	minHeight: 477.6,
	backgroundColor: theme.palette.common.white,
	borderRadius: theme.spacing(2),

	"&:not(:last-child)": {
		marginBottom: theme.spacing(8),

		[theme.breakpoints.down("md")]: {
			marginBottom: theme.spacing(4),
		},
	},

	[theme.breakpoints.down("md")]: {
		borderRadius: 0,
	},
	[theme.breakpoints.down("sm")]: {
		padding: theme.spacing(4),
	},
}));

interface ExternalTask {
	_id: string;
	dueAt: Date;
	startedAt: string;
	organizationId: string;
	userId: string;
	taskId: string;
	externalAssignees: {
		email: string;
		name: string;
	}[];
	completedAt: string;
	completedBy: string;
	task: {
		_id: string;
		name: string;
		description: string;
	};
	user: {
		_id: string;
		name: string;
		email: string;
		profilePictureUrl: string;
	};
}

export const ExternalTaskPage = () => {
	const params = useParams();

	const { formatMessage } = useIntl();

	const { enqueueSnackbar } = useSnackbar();

	const theme = useTheme();

	const isLight = getInvertColor(theme.palette.primary.main) === theme.palette.dirtyWhite["2000"];

	const [loading, setLoading] = useState(false);
	const [externalTasks, setExternalTasks] = useState<ExternalTask[]>([]);

	const getExternalTasks = useCallback(async () => {
		setLoading(true);
		try {
			const response = await apiClient<ExternalTask[]>(`user-tasks/external/${params.base64Email}`);

			if (response && response.data.length > 0) {
				setExternalTasks(response.data);
			}
		} catch (error) {
			const errorJson = (error as any)?.json;
			let errorMessage = formatMessage({
				id: "DASHBOARD.employee-journey.create_error",
			});
			if (errorJson.code) {
				errorMessage = getErrorMessage(errorJson.code);
			}
			enqueueSnackbar(errorMessage, { variant: "error" });
		} finally {
			setLoading(false);
		}
	}, [formatMessage, enqueueSnackbar, params]);

	useEffect(() => {
		if (params.slug) {
			localStorage.setItem(localStorageOrgSlugKey, params.slug);
		}

		if (params.base64Email) {
			getExternalTasks();
		}
	}, [params, getExternalTasks]);

	return (
		<OuterContainer id="ExternalTask">
			<Helmet>
				<title>
					{formatMessage({
						id: "EXTERNAL.user-task.header",
						defaultMessage: "Header",
					})}
				</title>
			</Helmet>
			<Container component="section">
				<Box display="flex" alignItems="flex-start" gap={2}>
					<Box width={theme.typography.pxToRem(28)} height={theme.typography.pxToRem(28)}>
						<PeopleIcon width={theme.typography.pxToRem(28)} height={theme.typography.pxToRem(28)} />
					</Box>

					<Box display="flex" flexDirection="column">
						<Typography
							variant="xl"
							sx={{
								fontWeight: 800,
								color: (theme) =>
									isLight ? darken(theme.palette.primary.main, 0.5) : theme.palette.primary.main,
								[theme.breakpoints.down("sm")]: {
									...theme.typography.md,
									fontWeight: 800,
								},
							}}
						>
							{formatMessage({ id: "EXTERNAL.user-task.header", defaultMessage: "Header" })}
						</Typography>
						<Typography
							component="p"
							variant="md"
							sx={{
								[theme.breakpoints.down("sm")]: {
									...theme.typography.sm,
								},
							}}
						>
							{formatMessage({
								id: "EXTERNAL.user-task.description",
								defaultMessage:
									"Lorem ipsum dolor sit, amet consectetur adipisicing elit. Unde voluptates sint aliquam, ipsa dolor praesentium. Modi quasi nobis, laudantium omnis, consequatur rerum quod voluptates, natus fuga quidem sequi itaque dolorem.",
							})}
						</Typography>
					</Box>
				</Box>

				<Stack
					direction="row"
					gap={4}
					pt={6}
					className="scrollbar"
					sx={{ overflow: "auto", scrollbarGutter: "stable" }}
				>
					{loading ? (
						<Preloader />
					) : (
						externalTasks.map((task) => (
							<TaskActivityCard
								key={task._id}
								// @ts-ignore
								activity={{
									dueDate: task.dueAt,
									title: task.task.name,
									status: task.completedAt ? ActivityStatus.COMPLETED : ActivityStatus.IN_PROGRESS,
									type: JourneyActivityType.TASK,
									description: task.task.description,
									userTaskId: task._id,
									user: {
										_id: task.user._id,
										name: task.user.name,
										email: task.user.email,
										profilePictureUrl: task.user.profilePictureUrl,
									},
								}}
								refetchExternalTasks={getExternalTasks}
							/>
						))
					)}
				</Stack>
			</Container>
		</OuterContainer>
	);
};
